import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { userApi } from '@/api/user'
import { setApiToken, clearApiToken } from '@/api'
import type { User, LoginParams, RegisterParams, LoginResponse } from '@/types/user'

export const useUserStore = defineStore('user', () => {
  // 状态
  const token = ref<string>('')
  const refreshToken = ref<string>('')
  const userInfo = ref<User | null>(null)
  const permissions = ref<string[]>([])
  const roles = ref<string[]>([])

  // 计算属性
  const isLoggedIn = computed(() => !!token.value && !!userInfo.value)
  // 优先使用 userType 判断,其次使用 roles 数组
  const isAdmin = computed(() => {
    // 方式1: 通过 userType 判断 (0为管理员, 1为个人用户, 2为企业用户)
    if (userInfo.value?.userType === 0) {
      return true
    }
    // 方式2: 通过 roles 数组判断 (向后兼容)
    return roles.value.includes('admin') || roles.value.includes('ADMIN')
  })
  const userName = computed(() => userInfo.value?.nickname || userInfo.value?.username || '')
  const userAvatar = computed(() => userInfo.value?.avatarUrl || '')

  // 登录
  const login = async (loginData: LoginResponse): Promise<void> => {
    try {
      // 先强制清除旧token，防止旧token被发送
      clearApiToken()
      localStorage.removeItem('api-token')

      // 保存令牌和用户信息
      token.value = loginData.accessToken
      refreshToken.value = loginData.refreshToken || ''
      userInfo.value = loginData.userInfo
      permissions.value = loginData.permissions || []
      roles.value = loginData.roles || []

      // 立即写入新token，确保后续请求使用新token
      setApiToken(loginData.accessToken)
    } catch (error) {
      throw error
    }
  }

  // 注册
  const register = async (params: RegisterParams): Promise<void> => {
    try {
      await userApi.register(params)
    } catch (error) {
      throw error
    }
  }

  // 登出（仅清除本地状态，后端无需通知）
  const logout = async (): Promise<void> => {
    clearUserData()
  }

  // 刷新令牌
  const refreshAccessToken = async (): Promise<string> => {
    try {
      const response = await userApi.refreshToken(refreshToken.value)
      const data: LoginResponse = response.data.data

      token.value = data.accessToken
      refreshToken.value = data.refreshToken || refreshToken.value
      userInfo.value = data.userInfo

      setApiToken(data.accessToken)
      return data.accessToken
    } catch (error) {
      // 刷新失败，清除用户数据
      clearUserData()
      throw error
    }
  }

  // 获取用户信息
  const getUserInfo = async (): Promise<void> => {
    if (!userInfo.value?.id) return

    try {
      const response = await userApi.getUserInfo(userInfo.value.id)
      userInfo.value = response.data.data
    } catch (error) {
      console.error('获取用户信息失败:', error)
    }
  }

  // 更新用户信息
  const updateUserInfo = async (data: Partial<User>): Promise<void> => {
    if (!userInfo.value?.id) return

    try {
      await userApi.updateUserInfo(userInfo.value.id, data)
      // 更新本地用户信息 - 创建新对象以触发响应式更新
      userInfo.value = { ...userInfo.value, ...data }
    } catch (error) {
      throw error
    }
  }

  // 修改密码(需先获取手机/邮箱验证码)
  const changePassword = async (
    oldPassword: string,
    newPassword: string,
    verifyChannel: 'PHONE' | 'EMAIL',
    verifyCode: string
  ): Promise<void> => {
    if (!userInfo.value?.id) return

    try {
      await userApi.changePassword(userInfo.value.id, oldPassword, newPassword, verifyChannel, verifyCode)
    } catch (error) {
      throw error
    }
  }

  // 检查权限
  const hasPermission = (permission: string): boolean => {
    return permissions.value.includes(permission)
  }

  // 检查角色
  const hasRole = (role: string): boolean => {
    return roles.value.includes(role)
  }

  // 检查是否有任一权限
  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => hasPermission(permission))
  }

  // 检查是否有任一角色
  const hasAnyRole = (roleList: string[]): boolean => {
    return roleList.some(role => hasRole(role))
  }

  // 清除用户数据
  const clearUserData = (): void => {
    token.value = ''
    refreshToken.value = ''
    userInfo.value = null
    permissions.value = []
    roles.value = []
    // 使用API模块的clearApiToken方法,确保完全清理
    clearApiToken()
  }

  // 初始化（在应用启动时调用）
  const init = (): void => {
    // token.value 由 pinia-plugin-persistedstate 从 localStorage 恢复
    // 同步到 api-token key，供 axios 拦截器使用
    if (token.value) {
      setApiToken(token.value)
    }
  }

  return {
    // 状态
    token,
    refreshToken,
    userInfo,
    permissions,
    roles,

    // 计算属性
    isLoggedIn,
    isAdmin,
    userName,
    userAvatar,

    // 方法
    login,
    register,
    logout,
    refreshAccessToken,
    getUserInfo,
    updateUserInfo,
    changePassword,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    clearUserData,
    init
  }
}, {
  persist: {
    // 只持久化关键数据
    paths: ['token', 'refreshToken', 'userInfo', 'permissions', 'roles']
  }
})
